import { useCallback, useRef, useEffect, useState, useMemo } from 'react';
import { X } from 'lucide-react';
import { usePviStore } from '../../stores/usePviStore';
import { useSimulationStore } from '../../stores/useSimulationStore';
import { useAdversarialStore } from '../../stores/useAdversarialStore';
import { useDiagnosticStore } from '../../stores/useDiagnosticStore';
import { useProjectStore } from '../../stores/useProjectStore';
import { GraphRenderer } from './GraphRenderer';
import { ValueTableView } from './ValueTableView';
import { JsonView } from './JsonView';
import { SimulationPanel } from './SimulationPanel';
import { AdversarialPanel } from './AdversarialPanel';
import { DiagnosticBlockBanner } from './DiagnosticBlockBanner';
import { FeeBreakdownPanel } from '../panels/FeeBreakdownPanel';
import { analyzeFees } from '../../editor/feeEstimator';
import styles from './PviGraphPanel.module.css';
import simStyles from './Simulation.module.css';
import advStyles from './Adversarial.module.css';

const TABS = [
  { id: 'graph' as const, label: 'Graph' },
  { id: 'table' as const, label: 'Value Table' },
  { id: 'json' as const, label: 'Raw JSON' },
];

export function PviGraphPanel() {
  const panelOpen = usePviStore(s => s.panelOpen);
  const panelWidth = usePviStore(s => s.panelWidth);
  const activeTab = usePviStore(s => s.activeTab);
  const compileResult = usePviStore(s => s.compileResult);
  const setPanelOpen = usePviStore(s => s.setPanelOpen);
  const setPanelWidth = usePviStore(s => s.setPanelWidth);
  const setActiveTab = usePviStore(s => s.setActiveTab);

  const simMode = useSimulationStore(s => s.mode);
  const setSimMode = useSimulationStore(s => s.setMode);

  const advMode = useAdversarialStore(s => s.mode);
  const setAdvMode = useAdversarialStore(s => s.setMode);
  const advResult = useAdversarialStore(s => s.result);

  const diagResult = useDiagnosticStore(s => s.result);

  const activeProject = useProjectStore(s => s.activeProject);
  const activeFileId = useProjectStore(s => s.activeFileId);

  const feeSummary = useMemo(() => {
    const file = activeProject?.files.find(f => f.id === activeFileId);
    const source = file?.content ?? '';
    if (!source) return null;
    const name = file?.name?.replace(/\.mist$/, '') ?? 'Contract';
    return analyzeFees(source, name).summary;
  }, [compileResult, activeProject, activeFileId]);

  const resizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  // Convert rem to px for resize math
  const remToPx = useCallback(() => parseFloat(getComputedStyle(document.documentElement).fontSize) || 16, []);

  // Resize handlers
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    resizing.current = true;
    startX.current = e.clientX;
    startWidth.current = panelWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    e.preventDefault();
  }, [panelWidth]);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!resizing.current) return;
      const deltaPx = startX.current - e.clientX;
      const deltaRem = deltaPx / remToPx();
      setPanelWidth(startWidth.current + deltaRem);
    };
    const onMouseUp = () => {
      if (resizing.current) {
        resizing.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [setPanelWidth, remToPx]);

  // ----- Vertical resize for sim/adv bottom panels -----
  const [simBottomH, setSimBottomH] = useState(420);
  const [advBottomH, setAdvBottomH] = useState(420);
  const vResizing = useRef<'sim' | 'adv' | null>(null);
  const vStartY = useRef(0);
  const vStartH = useRef(0);

  const onVResizeDown = useCallback((which: 'sim' | 'adv') => (e: React.MouseEvent) => {
    vResizing.current = which;
    vStartY.current = e.clientY;
    vStartH.current = which === 'sim' ? simBottomH : advBottomH;
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    e.preventDefault();
  }, [simBottomH, advBottomH]);

  useEffect(() => {
    const onVMove = (e: MouseEvent) => {
      if (!vResizing.current) return;
      const delta = vStartY.current - e.clientY;
      const newH = Math.max(100, Math.min(window.innerHeight * 0.65, vStartH.current + delta));
      if (vResizing.current === 'sim') setSimBottomH(newH);
      else setAdvBottomH(newH);
    };
    const onVUp = () => {
      if (vResizing.current) {
        vResizing.current = null;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
    document.addEventListener('mousemove', onVMove);
    document.addEventListener('mouseup', onVUp);
    return () => {
      document.removeEventListener('mousemove', onVMove);
      document.removeEventListener('mouseup', onVUp);
    };
  }, []);

  if (!panelOpen) return null;

  const { status, graph, warnings, stale } = compileResult;
  const displayGraph = graph;
  const isStale = stale;

  // Deploy status — from diagnostic model
  const isDeployable = status === 'success' && graph && !diagResult.isDeployBlocked;
  const isSimBlocked = diagResult.isSimulationBlocked;

  return (
    <div className={styles.panel} style={{ width: `${panelWidth}rem` }} data-testid="pvi-panel">
      {/* Resize handle */}
      <div className={styles.resizeHandle} onMouseDown={onMouseDown} />

      {/* Header */}
      <div className={styles.header}>
        <span className={styles.title}>PVI Graph</span>

        {/* Mode toggle */}
        <div className={simStyles.modeToggle}>
          <button
            className={`${simStyles.modeBtn} ${simMode === 'view' && advMode === 'off' ? simStyles.modeBtnActive : ''}`}
            onClick={() => { setSimMode('view'); setAdvMode('off'); }}
          >
            View
          </button>
          <button
            className={`${simStyles.modeBtn} ${simMode === 'simulation' && advMode === 'off' ? simStyles.modeBtnActive : ''}`}
            onClick={() => { setSimMode('simulation'); setAdvMode('off'); }}
            disabled={!displayGraph || displayGraph.nodes.length === 0 || isSimBlocked}
            title={isSimBlocked ? 'Simulation blocked by compile errors' : undefined}
          >
            Simulate
          </button>
          <button
            className={`${simStyles.modeBtn} ${advMode === 'adversarial' ? simStyles.modeBtnActive : ''}`}
            onClick={() => { setSimMode('simulation'); setAdvMode('adversarial'); }}
            disabled={!displayGraph || displayGraph.nodes.length === 0 || isSimBlocked}
            title={isSimBlocked ? 'Adversarial blocked by compile errors' : undefined}
            style={advMode === 'adversarial' ? { background: '#ef4444', color: '#fff' } : undefined}
          >
            Adversarial
          </button>
        </div>

        <div className={styles.tabs}>
          {TABS.map(t => (
            <button
              key={t.id}
              className={`${styles.tab} ${activeTab === t.id ? styles.tabActive : ''}`}
              onClick={() => setActiveTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className={styles.headerRight}>
          {status === 'success' && (
            <span className={`${styles.deployBadge} ${isDeployable && !(advResult && advResult.totalFailures > 0) ? styles.deployOk : styles.deployFail}`}>
              {advResult && advResult.totalFailures > 0
                ? '⚠ Adversarial Risks'
                : isDeployable ? '✔ Deployable' : '✖ Cannot Deploy'}
            </span>
          )}
          <button className={styles.closeBtn} onClick={() => setPanelOpen(false)} title="Close PVI Panel" aria-label="Close PVI panel">
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className={styles.body} style={{ display: 'flex', flexDirection: 'column' }}>
        {/* No graph at all */}
        {status === 'idle' && (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>◇</div>
            <p>Open a .mist file to see its PVI value-flow graph</p>
          </div>
        )}

        {/* Compile error */}
        {status === 'error' && !displayGraph && (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>✖</div>
            <p>Cannot build PVI graph</p>
            <DiagnosticBlockBanner
              diagnostics={diagResult.diagnostics.filter(d => d.graphBlocking)}
              label="Graph generation blocked"
            />
          </div>
        )}

        {/* Compiling */}
        {status === 'compiling' && !displayGraph && (
          <div className={styles.emptyState}>
            <p>Compiling...</p>
          </div>
        )}

        {/* Empty graph (valid contract, no value ops) */}
        {displayGraph && displayGraph.nodes.length === 0 && (
          <>
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>◇</div>
              <p>Contract has no value operations</p>
              <p style={{ fontSize: 11, opacity: 0.7 }}>
                Add hold, pay, release, refund, split, mint, or burn operations to see the PVI graph
              </p>
              {warnings.length > 0 && (
                <ul className={styles.warningList}>
                  {warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              )}
            </div>
          </>
        )}

        {/* Actual graph display */}
        {displayGraph && displayGraph.nodes.length > 0 && (
          <>
            {advMode === 'adversarial' ? (
              <div className={advStyles.advPanel}>
                <div className={advStyles.advGraphArea}>
                  {activeTab === 'graph' && <GraphRenderer graph={displayGraph} stale={isStale || stale} />}
                  {activeTab === 'table' && <ValueTableView graph={displayGraph} />}
                  {activeTab === 'json' && <JsonView graph={displayGraph} />}
                </div>
                <div className={styles.vResizeHandle} onMouseDown={onVResizeDown('adv')} />
                <div className={advStyles.advBottomArea} style={{ height: advBottomH, minHeight: 100, maxHeight: '65%' }}>
                  <AdversarialPanel graph={displayGraph} />
                </div>
              </div>
            ) : simMode === 'simulation' ? (
              <div className={simStyles.simPanel}>
                <div className={simStyles.simGraphArea}>
                  {activeTab === 'graph' && <GraphRenderer graph={displayGraph} stale={isStale || stale} />}
                  {activeTab === 'table' && <ValueTableView graph={displayGraph} />}
                  {activeTab === 'json' && <JsonView graph={displayGraph} />}
                </div>
                <div className={styles.vResizeHandle} onMouseDown={onVResizeDown('sim')} />
                <div className={simStyles.simBottomArea} style={{ height: simBottomH, minHeight: 100, maxHeight: '65%' }}>
                  <SimulationPanel graph={displayGraph} />
                </div>
              </div>
            ) : (
              <>
                {activeTab === 'graph' && (
                  <GraphRenderer graph={displayGraph} stale={isStale || stale} />
                )}
                {activeTab === 'table' && (
                  <ValueTableView graph={displayGraph} />
                )}
                {activeTab === 'json' && (
                  <JsonView graph={displayGraph} />
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Permanent fee panel at bottom of PVI panel */}
      <div style={{
        borderTop: '1px solid var(--color-border, #2a2a3e)',
        flexShrink: 0,
        maxHeight: '40%',
        overflow: 'auto',
      }}>
        <FeeBreakdownPanel summary={feeSummary} />
      </div>
    </div>
  );
}
